import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Typography from '@mui/material/Typography'
import { Button, ButtonGroup, Chip, Stack, Skeleton } from '@mui/material'
import { DiscountLabel, ProductName, BrandName, StockLabel } from '../styledComponents/StyledItemComponents'
import { Currency } from 'react-intl-number-format'
import StyledLink from '../StyledLink'
import DeleteIcon from '@mui/icons-material/Delete'
import { useAppContext } from '../../context/AppProvider'
import PropTypes from 'prop-types'
import { useCart } from '../../hooks/useCart'
import { debounce } from 'lodash'
import { SHIPPING_PRICE, canIncreaseCartQuantity, getAvailabilityBreakdown, getCollectionBadge, getDisplayBrandName, getEffectivePrice, getStockLabel } from '../../utils/itemPresentation'

export default function MediaControlCard ({ item, showShipping = true }) {
  const itemData = useRef(item)
  const { state } = useAppContext()
  const theme = useTheme()
  const [counter, setCounter] = useState(item.count)
  const { removeItemFromCart, updateCartItem } = useCart()
  const collectionBadge = getCollectionBadge(item)
  const stockLabel = getStockLabel(item)
  const finalPrice = getEffectivePrice(item)
  const availability = getAvailabilityBreakdown(item, counter)
  const displayBrandName = getDisplayBrandName(item)

  useEffect(() => {
    itemData.current = item
    setCounter(Number(item.count || 0))
  }, [item])

  const debounceUpdateCartItem = useCallback(debounce((item, count) => {
    updateCartItem(item, count)
  }, 500), [])

  return (
    <Card
      sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'space-between' }, width: { xs: '250px', sm: 'auto' }, minHeight: { xs: '470px', sm: '250px', md: '160px' }, marginTop: '20px', padding: '0 30px', flexDirection: { xs: 'column', sm: 'row' } }}
    >
      <Box display='flex' sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        {state.cart.loading
          ? (
            <Box sx={{ minWidth: '220px', minHeight: '450px' }}>
              <Skeleton variant='rectangular' width={210} height={118} />
              <Box>
                <Skeleton variant='text' sx={{ width: '50%', fontSize: '22px' }} />
                <Skeleton animation='wave' sx={{ width: '75%' }} />
                <Skeleton animation='wave' sx={{ width: '75%' }} />
                <Skeleton variant='text' sx={{ width: '50%', fontSize: '22px' }} />
              </Box>
            </Box>
            )
          : (
            <>
              <Box sx={{ position: 'relative', minWidth: '150px', display: 'flex', justifyContent: 'center' }}>
                {item.brand?.logo
                  ? (
                    <Box
                      component='img'
                      src={item.brand.logo}
                      alt={item.brand?.name || 'Brand logo'}
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        width: '78px',
                        height: '26px',
                        objectFit: 'contain',
                        objectPosition: 'left center',
                        zIndex: 2,
                        backgroundColor: 'rgba(255,255,255,0.92)',
                        borderRadius: '8px',
                        px: 0.75,
                        py: 0.25
                      }}
                    />
                    )
                  : null}
                <CardMedia
                  component='img'
                  sx={{ height: '100%', width: '150px', objectFit: 'contain' }}
                  image={item.cover}
                  alt='Product image'
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: '1' }}>
                  {item && (
                    <StyledLink to={{ pathname: `/items/${item._id}` }}>
                      <ProductName sx={{ fontSize: '14px', WebkitLineClamp: 2, textOverflow: 'ellipsis' }}>
                        {item.name}
                      </ProductName>
                    </StyledLink>
                  )}
                  {!item.brand?.logo && displayBrandName && (
                    <BrandName sx={{ fontSize: '13px', marginTop: '8px', marginBottom: '10px' }}>
                      {displayBrandName}
                    </BrandName>
                  )}
                  {collectionBadge && (
                    <DiscountLabel sx={{ top: 'auto', left: 0, marginBottom: '10px' }}>
                      {collectionBadge}
                    </DiscountLabel>
                  )}
                  <StockLabel sx={{ position: 'relative', left: '0px', paddingLeft: '10px' }}>
                    {stockLabel}
                  </StockLabel>
                  <Chip
                    label={`Cantitate in cos: ${counter}`}
                    size='small'
                    sx={{
                      mt: 1.2,
                      fontSize: '11px',
                      fontWeight: 700,
                      color: theme.palette.fontColor.main,
                      backgroundColor: theme.palette.secondary.main,
                      border: `1px solid ${theme.palette.borderColor.main}`,
                      '& .MuiChip-label': { px: 1.1 }
                    }}
                  />
                  <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap sx={{ mt: 1 }}>
                    {availability.inStockQuantity > 0 && (
                      <Chip
                        label={`In stoc: ${availability.inStockQuantity}`}
                        size='small'
                        sx={{
                          fontSize: '11px',
                          fontWeight: 800,
                          color: '#008000',
                          backgroundColor: 'rgba(0, 128, 0, 0.11)',
                          '& .MuiChip-label': { px: 1.1 }
                        }}
                      />
                    )}
                    {availability.onOrderQuantity > 0 && (
                      <Chip
                        label={`La comanda: ${availability.onOrderQuantity}`}
                        size='small'
                        sx={{
                          fontSize: '11px',
                          fontWeight: 800,
                          color: theme.palette.accent.main,
                          backgroundColor: 'rgba(226, 6, 19, 0.08)',
                          border: `1px solid ${theme.palette.accent.main}`,
                          '& .MuiChip-label': { px: 1.1 }
                        }}
                      />
                    )}
                  </Stack>
                  {availability.onOrderQuantity > 0 && (
                    <Typography variant='caption' sx={{ display: 'block', color: theme.palette.fontColor.medium, fontSize: '11px', fontWeight: 600, marginTop: '6px' }}>
                      Bucatile la comanda pot avea termen de livrare mai mare.
                    </Typography>
                  )}
                  {showShipping && (
                    <Typography variant='body2' sx={{ color: theme.palette.fontColor.medium, marginTop: '10px' }}>
                      Transport: {SHIPPING_PRICE} lei
                    </Typography>
                  )}
                </CardContent>
              </Box>
            </>)}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Stack sx={{ padding: '15px' }}>
          <Typography variant='h6' sx={{ color: theme.palette.accent.main, fontSize: '16px', fontWeight: 600 }}>
            <Currency locale='ro-RO' currency='RON'>
              {finalPrice * counter}
            </Currency>
          </Typography>

          <ButtonGroup size='small' aria-label='small outlined button group'>
            <Button
              variant='outlined'
              aria-label='Scade cantitatea'
              disabled={counter <= 0}
              onClick={() => {
                setCounter((currentCount) => {
                  const newCount = currentCount - 1
                  if (newCount === 0) {
                    removeItemFromCart(itemData)
                  } else {
                    debounceUpdateCartItem(itemData, { count: newCount })
                  }
                  return newCount
                })
              }}
              color='fontColor'
              sx={{ minWidth: '52px', fontSize: '20px', fontWeight: 700 }}
            >
              -
            </Button>
            <Button
              variant='outlined'
              disabled
              color='fontColor'
              sx={{
                minWidth: '64px',
                fontSize: '20px',
                fontWeight: 800,
                color: `${theme.palette.fontColor.dark} !important`,
                opacity: '1 !important',
                backgroundColor: theme.palette.secondary.main
              }}
            >
              {counter}
            </Button>
            <Button
              variant='outlined'
              aria-label='Creste cantitatea'
              disabled={!canIncreaseCartQuantity(item, counter)}
              onClick={() => {
                setCounter((currentCount) => {
                  const newCount = currentCount + 1
                  debounceUpdateCartItem(itemData, { count: newCount })
                  return newCount
                })
              }}
              color='fontColor'
              sx={{ minWidth: '52px', fontSize: '20px', fontWeight: 700 }}
            >
              +
            </Button>
          </ButtonGroup>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant='outlined'
              size='small'
              onClick={() => removeItemFromCart(itemData)}
              startIcon={<DeleteIcon />}
              color='success'
              sx={{ color: 'success', borderRadius: '16px', height: 'auto', marginTop: '10px', textTransform: 'none' }}
            >
              Șterge
            </Button>
          </Box>
        </Stack>
      </Box>
    </Card>
  )
}

MediaControlCard.propTypes = {
  item: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  showShipping: PropTypes.bool
}
